/* PmosProgramHome — 12-week, 5-phase PMOS Reset orchestration view (iter445).
 *
 * Replaces the generic Journey tab when active program = pmos-reset.
 * Pulls from /api/wh/pmos-program/* — orchestrates AI Nutrition + Health
 * Intelligence + Women's Health into one guided patient journey.
 *
 * Sections:
 *  - Top hero: Week N of 12, Phase title, Top Driver, Today's Action, Weekly Goal, Recent Improvement
 *  - Phase Progress strip (5 phases)
 *  - Phase Detail (cards swap by phase): PMOS Profile · Driver Card · Nutrition · Intel · Cycle · Long-term
 *  - PMOS Insights (Observation + Interpretation + Action + Confidence)
 *  - Progress KPIs (primary + secondary)
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Loader2, Sparkles, Activity, Heart, Target, TrendingUp, TrendingDown,
  Minus, CheckCircle2, Circle, BookOpen, Lightbulb, Compass, FlaskConical,
  Salad, ChevronRight,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  DriverExplainerPopover, SourceAttributionChip, ConfidenceIndicator,
  PhenotypeLessonSheet, PmosAssessmentLink, ScoreBreakdownPopover,
} from "./PmosV3Extras";

const PHASE_ICON = { 1: Compass, 2: Target, 3: Activity, 4: Heart, 5: Sparkles };

export default function PmosProgramHome({ bg }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [home, setHome] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePhase, setActivePhase] = useState(null);
  const [phaseData, setPhaseData] = useState(null);
  const [phaseLoading, setPhaseLoading] = useState(false);
  const [insights, setInsights] = useState(null);
  const [progress, setProgress] = useState(null);

  const loadHome = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: h }, { data: i }, { data: pr }] = await Promise.all([
        axios.get(`${API}/wh/pmos-program/home`),
        axios.get(`${API}/wh/pmos-program/insights`),
        axios.get(`${API}/wh/pmos-program/progress`),
      ]);
      setHome(h);
      setInsights(i);
      setProgress(pr);
      if (h.is_active && !activePhase) setActivePhase(h.phase?.n || 1);
    } finally { setLoading(false); }
  }, [API, activePhase]);

  useEffect(() => { loadHome(); }, [loadHome]);

  // iter450: refresh data when PMOS assessment is saved anywhere in the page.
  useEffect(() => {
    const handler = () => { loadHome(); };
    window.addEventListener("pmos-assessment-saved", handler);
    return () => window.removeEventListener("pmos-assessment-saved", handler);
  }, [loadHome]);

  const loadPhase = useCallback(async (n) => {
    setPhaseLoading(true);
    try {
      const { data } = await axios.get(`${API}/wh/pmos-program/phase/${n}`);
      setPhaseData(data);
    } finally { setPhaseLoading(false); }
  }, [API]);

  useEffect(() => { if (activePhase) loadPhase(activePhase); }, [activePhase, loadPhase]);

  // iter450: reload current phase when assessment changes.
  useEffect(() => {
    const handler = () => { if (activePhase) loadPhase(activePhase); };
    window.addEventListener("pmos-assessment-saved", handler);
    return () => window.removeEventListener("pmos-assessment-saved", handler);
  }, [activePhase, loadPhase]);

  // Phase ref for scroll-into-view when activePhase changes (iter452: fix nav UX)
  const phaseDetailRef = useRef(null);
  const lastSelectedRef = useRef(null);
  useEffect(() => {
    if (activePhase && phaseDetailRef.current && lastSelectedRef.current !== null) {
      try { phaseDetailRef.current.scrollIntoView({ behavior: "smooth", block: "start" }); } catch { /* */ }
    }
    lastSelectedRef.current = activePhase;
  }, [activePhase]);

  const completeAction = async (actionId, completed) => {
    try {
      await axios.post(`${API}/wh/v3/actions/complete`, { action_id: actionId, completed });
      toast.success(completed ? (bg ? "Браво!" : "Done!") : (bg ? "Отбелязано" : "Updated"));
      loadHome();
    } catch { toast.error(bg ? "Грешка" : "Error"); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-pink-500" />
      </div>
    );
  }

  if (!home?.is_active) {
    return (
      <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"} data-testid="pmos-program-inactive">
        <CardContent className="p-6 text-center">
          <Sparkles className="w-7 h-7 mx-auto mb-2 text-pink-500" />
          <p className={`text-sm ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
            {bg ? home?.cta_bg : home?.cta_en}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="pmos-program-home">
      <HeroCard home={home} bg={bg} isDark={isDark} onAction={completeAction} />

      {/* iter500: phase details + insights + progress KPIs hidden behind one expander
          (patient-centric consolidation). User sees Hero + Action + Wins by default. */}
      <details className={`rounded-2xl border ${isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"}`} data-testid="pmos-deep-details">
        <summary className="flex items-center justify-between p-3 cursor-pointer list-none">
          <span className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
            {bg ? "Виж подробности за програмата (фази, скорове, профил)" : "Program details (phases, scores, profile)"}
          </span>
          <ChevronRight className={`w-4 h-4 ${isDark ? "text-slate-500" : "text-[#8F9990]"}`} />
        </summary>
        <div className="p-3 pt-0 space-y-4">
          <PhaseStrip
            phases={home.phase ? home : null}
            currentPhaseN={home.phase?.n}
            activePhase={activePhase}
            setActivePhase={setActivePhase}
            bg={bg} isDark={isDark}
          />
          <div ref={phaseDetailRef}>
            <PhaseDetail data={phaseData} loading={phaseLoading} bg={bg} isDark={isDark} API={API} />
          </div>
          <PmosInsights insights={insights} bg={bg} isDark={isDark} />
          <ProgressKpis progress={progress} bg={bg} isDark={isDark} />
        </div>
      </details>
    </div>
  );
}

/* ───── Hero ───── */
function HeroCard({ home, bg, isDark, onAction }) {
  const Icon = PHASE_ICON[home.phase?.n] || Sparkles;
  const ta = home.today_action;
  const wg = home.weekly_goal;
  const ri = home.recent_improvement;
  return (
    <Card className={isDark ? "bg-gradient-to-br from-pink-950/60 to-fuchsia-950/40 border-pink-900" : "bg-gradient-to-br from-pink-50 to-fuchsia-50 border-pink-100"} data-testid="pmos-program-hero">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-pink-500 to-fuchsia-500 flex items-center justify-center text-white shadow-lg">
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <p className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-pink-300" : "text-pink-700"}`}>
                PMOS Reset · {bg ? "Седмица" : "Week"} {home.current_week} / {home.total_weeks}
              </p>
              <h2 className="wh-heading text-2xl">
                {bg ? home.phase?.title_bg : home.phase?.title_en}
              </h2>
              <p className={`text-xs ${isDark ? "text-pink-100/80" : "text-pink-900/80"}`}>
                {bg ? home.phase?.goal_bg : home.phase?.goal_en}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Top Driver */}
          {home.top_driver && (
            <DriverExplainerPopover driverKey={home.top_driver.key}>
              <div className={`p-3 rounded-2xl text-left w-full ${isDark ? "bg-slate-900/50" : "bg-white/70"}`} data-testid="pmos-hero-driver">
                <p className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
                  {bg ? "Топ фактор" : "Top driver"}
                </p>
                <p className={`text-base font-bold ${isDark ? "text-white" : "text-[#2A342B]"}`}>
                  {bg ? home.top_driver.label_bg : home.top_driver.label_en}
                </p>
                <p className={`text-[10px] mt-1 ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
                  {bg ? `Принос ${home.top_driver.contribution_pct}%` : `Contribution ${home.top_driver.contribution_pct}%`}
                </p>
                <div className="mt-1">
                  <ConfidenceIndicator level={home.top_driver.confidence} levelLabel={{high:"Висока",moderate:"Умерена",low:"Ниска"}[home.top_driver.confidence]} />
                </div>
              </div>
            </DriverExplainerPopover>
          )}

          {/* Weekly Goal */}
          <div className={`p-3 rounded-2xl ${isDark ? "bg-slate-900/50" : "bg-white/70"}`} data-testid="pmos-hero-goal">
            <p className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
              {bg ? "Цел за седмицата" : "Weekly goal"}
            </p>
            <p className={`text-sm font-bold ${isDark ? "text-white" : "text-[#2A342B]"}`}>
              {bg ? wg?.goal_bg : wg?.goal_en}
            </p>
            <p className={`text-[10px] mt-1 ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>
              {bg ? "Цел" : "Target"}: {wg?.target_value}
            </p>
            {wg?.target_kpi === "data_coverage" && (
              <PmosAssessmentLink className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-pink-600 hover:text-pink-700 underline">
                {bg ? "Попълни PMOS оценка" : "Complete PMOS assessment"}
              </PmosAssessmentLink>
            )}
          </div>

          {/* Today's Action */}
          {ta && (
            <button
              onClick={() => onAction(ta.id, !ta.completed_today)}
              className={`text-left p-3 rounded-2xl border transition ${
                ta.completed_today
                  ? isDark ? "bg-emerald-950/40 border-emerald-700" : "bg-emerald-50 border-emerald-200"
                  : isDark ? "bg-slate-900/50 border-slate-700 hover:border-pink-500" : "bg-white/70 border-pink-200 hover:border-pink-400"
              }`}
              data-testid="pmos-hero-action"
            >
              <div className="flex items-center gap-1.5 mb-1">
                {ta.completed_today
                  ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                  : <Circle className="w-3.5 h-3.5 text-pink-500" />}
                <p className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
                  {bg ? "Днешно действие" : "Today's action"}
                </p>
              </div>
              <p className={`text-sm font-bold ${isDark ? "text-white" : "text-[#2A342B]"}`}>
                {bg ? ta.bg : ta.en}
              </p>
            </button>
          )}

          {/* Recent Improvement */}
          {ri && (
            <div className={`p-3 rounded-2xl ${isDark ? "bg-slate-900/50" : "bg-white/70"}`} data-testid="pmos-hero-improvement">
              <p className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
                {bg ? "Скорошно подобрение" : "Recent improvement"}
              </p>
              <p className={`text-sm font-bold flex items-center gap-1.5 ${ri.is_positive ? "text-emerald-500" : "text-amber-500"}`}>
                {ri.direction === "up" ? <TrendingUp className="w-3.5 h-3.5" /> :
                 ri.direction === "down" ? <TrendingDown className="w-3.5 h-3.5" /> :
                 <Minus className="w-3.5 h-3.5" />}
                {bg ? ri.metric_bg : ri.metric_en}
              </p>
              <p className={`text-[10px] mt-1 ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
                {ri.from_value} → {ri.to_value}
                {ri.delta_pct != null && ` (${ri.delta_pct > 0 ? "+" : ""}${ri.delta_pct}%)`}
              </p>
            </div>
          )}
        </div>

        <p className={`text-[10px] italic mt-3 ${isDark ? "text-pink-200/60" : "text-pink-900/60"}`}>
          {bg ? home.disclaimer_bg : home.disclaimer_en}
        </p>
      </CardContent>
    </Card>
  );
}

/* ───── Phase strip ───── */
function PhaseStrip({ currentPhaseN, activePhase, setActivePhase, bg, isDark }) {
  const phases = [
    { n: 1, bg: "Опознай тялото си",         en: "Get to know your body" },
    { n: 2, bg: "Какво ти влияе",            en: "What affects you" },
    { n: 3, bg: "Балансирай хранене и сън",  en: "Balance food & sleep" },
    { n: 4, bg: "Възстанови цикъла си",      en: "Restore your cycle" },
    { n: 5, bg: "Поддържай постигнатото",    en: "Keep what you built" },
  ];
  return (
    <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"}>
      <CardContent className="p-3 sm:p-4">
        <div className="grid grid-cols-5 gap-1.5" data-testid="pmos-phase-strip">
          {phases.map((p) => {
            const Icon = PHASE_ICON[p.n];
            const isCurrent = p.n === currentPhaseN;
            const isViewing = p.n === activePhase;
            const isPassed = p.n < currentPhaseN;
            return (
              <button
                key={p.n}
                type="button"
                onClick={() => setActivePhase(p.n)}
                className={`p-2 sm:p-2.5 rounded-xl text-center text-[10px] sm:text-xs font-bold flex flex-col items-center gap-1 transition ${
                  isViewing ? "bg-gradient-to-br from-pink-500 to-fuchsia-500 text-white shadow" :
                  isPassed ? "bg-emerald-500 text-white" :
                  isCurrent ? (isDark ? "bg-pink-950/50 text-pink-200 border border-pink-800" : "bg-pink-50 text-pink-700 border border-pink-200") :
                  isDark ? "bg-slate-900 text-slate-400 hover:bg-slate-800" : "bg-[#F5F3ED] text-[#5C665D] hover:bg-[#EBE6DA]"
                }`}
                data-testid={`pmos-phase-${p.n}`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{bg ? p.bg : p.en}</span>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

/* ───── Phase detail ───── */
function PhaseDetail({ data, loading, bg, isDark, API }) {
  if (loading || !data) {
    return (
      <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"}>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-pink-500" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="pmos-phase-detail">
      <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"}>
        <CardContent className="p-4 sm:p-5">
          <p className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
            {bg ? `Фаза ${data.phase.n} · седмици ${data.phase.weeks_from}-${data.phase.weeks_to}` : `Phase ${data.phase.n} · weeks ${data.phase.weeks_from}-${data.phase.weeks_to}`}
          </p>
          <h3 className="wh-heading text-lg mb-1">{bg ? data.phase.title_bg : data.phase.title_en}</h3>
          <p className={`text-xs ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
            {bg ? data.phase.focus_bg : data.phase.focus_en}
          </p>
        </CardContent>
      </Card>

      {data.cards.map((c) => (
        <PhaseCard key={c.key} card={c} bg={bg} isDark={isDark} API={API} />
      ))}

      {data.lessons?.length > 0 && (
        <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"}>
          <CardContent className="p-4 sm:p-5">
            <p className={`text-[10px] uppercase tracking-wider font-bold mb-2 ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
              {bg ? "Уроци за тази фаза" : "Lessons for this phase"}
            </p>
            <div className="space-y-2">
              {data.lessons.map((l) => (
                <PhenotypeLessonSheet
                  key={l.id}
                  lessonId={l.id}
                  lessonTitle={bg ? l.title_bg : l.title_en}
                  lessonMin={l.min}
                  isDark={isDark}
                  bg={bg}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ───── Phase card (polymorphic by type) ───── */
function PhaseCard({ card, bg, isDark, API }) {
  const Icon = {
    profile: Sparkles, "driver-card": Target, nutrition: Salad,
    intel: FlaskConical, cycle: Heart, education: BookOpen,
  }[card.type] || Sparkles;

  return (
    <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"} data-testid={`pmos-card-${card.key}`}>
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-pink-500 to-fuchsia-500 flex items-center justify-center text-white">
              <Icon className="w-3.5 h-3.5" />
            </div>
            <div>
              <p className={`text-[9px] uppercase tracking-wider font-bold ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
                {card.source_module}
              </p>
              <h3 className="wh-heading text-base">{bg ? card.title_bg : card.title_en}</h3>
            </div>
          </div>
        </div>

        {card.type === "profile" && <ProfileCardBody data={card.data} bg={bg} isDark={isDark} />}
        {card.type === "driver-card" && <DriverCardBody data={card.data} bg={bg} isDark={isDark} />}
        {card.type === "nutrition" && <NutritionCardBody data={card.data} bg={bg} isDark={isDark} API={API} />}
        {card.type === "intel" && <IntelCardBody data={card.data} bg={bg} isDark={isDark} API={API} />}
        {card.type === "cycle" && <CycleCardBody data={card.data} bg={bg} isDark={isDark} API={API} />}
        {card.type === "education" && <EducationCardBody data={card.data} bg={bg} isDark={isDark} />}
      </CardContent>
    </Card>
  );
}

function ProfileCardBody({ data, bg, isDark }) {
  if (!data.has_data) {
    return <p className={`text-xs ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>{bg ? "Все още няма попълнена PMOS оценка." : "PMOS assessment not completed yet."}</p>;
  }
  // Each subscore + how it's computed (transparency for the user).
  const SUB_LABEL = {
    ovulation:        { bg: "Овулация",            en: "Ovulation",     src_bg: "Изчислено от: брой цикли годишно, симптоми на овулация" },
    metabolic:        { bg: "Метаболитно здраве",  en: "Metabolic",     src_bg: "Изчислено от: BMI, талия + лабораторни (HOMA-IR, кръвна захар)" },
    inflammation:     { bg: "Възпаление",          en: "Inflammation",  src_bg: "Изчислено от: храненето + лабораторни (hsCRP)" },
    androgen:         { bg: "Андрогени",           en: "Androgens",     src_bg: "Изчислено от: акне, окосмение + лабораторни (тестостерон, SHBG)" },
    nutrition:        { bg: "Хранене",             en: "Nutrition",     src_bg: "Изчислено от: Хранителен дневник (фибри, белтъци)" },
    lifestyle:        { bg: "Начин на живот",      en: "Lifestyle",     src_bg: "Изчислено от: сън, движение, стрес" },
    mental_wellbeing: { bg: "Емоционално",         en: "Mental",        src_bg: "Изчислено от: Дневник на настроението (стрес, мотивация)" },
  };
  const subs = Object.entries(data.subscores || {}).filter(([, v]) => v != null);
  // Heuristic: subscores that benefit from lab data
  const NEEDS_LABS = new Set(["metabolic", "inflammation", "androgen"]);
  const missingLabs = subs.filter(([k]) => NEEDS_LABS.has(k));

  const openLabUpload = () => {
    try { window.dispatchEvent(new CustomEvent("wh-open-tool", { detail: { tool: "lab-upload" } })); } catch { /* */ }
  };

  return (
    <div className="space-y-2">
      {data.phenotype && (
        <p className={`text-sm ${isDark ? "text-slate-200" : "text-[#2A342B]"}`}>
          <b>{bg ? "Фенотип:" : "Phenotype:"}</b> {data.phenotype}
        </p>
      )}
      {subs.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
          {subs.map(([k, v]) => {
            const lbl = SUB_LABEL[k] || { bg: k, en: k, src_bg: null };
            return (
              <ScoreBreakdownPopover key={k} metric={k} value={v}>
                <div className={`p-2 rounded-xl text-left w-full ${isDark ? "bg-slate-900/40 hover:bg-slate-900/60" : "bg-[#F5F3ED] hover:bg-[#EBE6DA]"} transition`} data-testid={`pmos-subscore-${k}`}>
                  <p className={`text-[10px] tracking-wider font-medium ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>{bg ? lbl.bg : lbl.en}</p>
                  <p className={`text-base font-bold ${isDark ? "text-white" : "text-[#2A342B]"}`}>{v} <span className="text-[10px] font-normal opacity-60">/ 100</span></p>
                  {bg && lbl.src_bg && (
                    <p className={`text-[9px] mt-1 leading-tight ${isDark ? "text-slate-500" : "text-[#8F9990]"}`}>{lbl.src_bg}</p>
                  )}
                </div>
              </ScoreBreakdownPopover>
            );
          })}
        </div>
      )}
      {/* Transparency banner about labs */}
      {bg && missingLabs.length > 0 && (
        <div className={`mt-2 p-2.5 rounded-xl border-l-4 border-amber-500 ${isDark ? "bg-amber-950/30" : "bg-amber-50"}`} data-testid="pmos-labs-cta">
          <p className={`text-[11px] mb-1 ${isDark ? "text-amber-200" : "text-amber-900"}`}>
            <b>Тези скорове са груби.</b> За точност добави лабораторни (хормони, HOMA-IR, hsCRP, ехография).
          </p>
          <button
            type="button"
            onClick={openLabUpload}
            className={`inline-flex items-center gap-1 text-[11px] font-bold underline ${isDark ? "text-amber-300" : "text-amber-700"}`}
            data-testid="pmos-labs-upload-btn"
          >
            Качи лабораторни данни →
          </button>
        </div>
      )}
      <SourceAttributionChip source="Класически изглед · PMOS оценка" freshness={null} isDark={isDark} />
      <PmosAssessmentLink className="mt-1 inline-flex items-center gap-1 text-[11px] text-pink-600 hover:text-pink-700">
        {bg ? "Обнови оценката" : "Update assessment"}
      </PmosAssessmentLink>
    </div>
  );
}

function DriverCardBody({ data, bg, isDark }) {
  return (
    <div className="space-y-2" data-testid="pmos-driver-card-body">
      {data.drivers.filter(d => d.value != null).map((d) => (
        <div key={d.key} className={`p-2.5 rounded-xl ${d.key === data.dominant_driver ? (isDark ? "bg-rose-950/30 border border-rose-800" : "bg-rose-50 border border-rose-200") : (isDark ? "bg-slate-900/40" : "bg-[#F5F3ED]")}`}>
          <div className="flex items-center justify-between mb-0.5">
            <span className={`text-sm font-bold ${d.key === data.dominant_driver ? (isDark ? "text-rose-200" : "text-rose-900") : (isDark ? "text-slate-200" : "text-[#2A342B]")}`}>
              {bg ? d.label_bg : d.label_en}
            </span>
            <span className={`text-[10px] font-bold ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
              {d.contribution_pct}% · {bg ? {high:"висока",moderate:"умерена",low:"ниска"}[d.confidence] : d.confidence}
            </span>
          </div>
          <div className={`h-1.5 rounded-full overflow-hidden mb-1 ${isDark ? "bg-slate-800" : "bg-white"}`}>
            <div className="h-full bg-gradient-to-r from-violet-500 to-pink-500" style={{ width: `${d.contribution_pct ?? 0}%` }} />
          </div>
          <p className={`text-[10px] ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>{d.evidence}</p>
        </div>
      ))}
    </div>
  );
}

function NutritionCardBody({ data, bg, isDark, API }) {
  const [actions, setActions] = useState([]);
  useEffect(() => {
    axios.get(`${API}/wh/pmos-program/nutrition-card`).then(({ data: d }) => setActions(d.pmos_actions || [])).catch(() => {});
  }, [API]);
  if (!data?.has_data) {
    return <p className={`text-xs ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>{bg ? "Все още няма данни от Хранителен дневник." : "No data from Meal Diary yet."}</p>;
  }
  const metrics = [
    { k: "nis",   label_bg: "Хранителен индекс", label_en: "Nutrient score",  val: data.nis,       unit: "/100", tip_bg: "Колко добре храненето стабилизира кръвната захар." },
    { k: "dil",   label_bg: "Възпалителен ефект", label_en: "Inflammation",   val: data.dil,       unit: "/45",  tip_bg: "Колко противовъзпалителни са храните ти." },
    { k: "uis",   label_bg: "Ултрапреработени",  label_en: "Ultra-processed", val: data.uis,       unit: "%",    tip_bg: "Дял ултрапреработени храни. По-малко = по-добре." },
    { k: "fiber", label_bg: "Фибри",             label_en: "Fiber",           val: data.fiber_g,   unit: "г" },
    { k: "protein", label_bg: "Белтъци",         label_en: "Protein",         val: data.protein_g, unit: "г" },
    { k: "satiety", label_bg: "Засищане",        label_en: "Satiety",         val: data.satiety,   unit: "/100" },
  ];
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
        {metrics.map((m) => (
          <div key={m.k} className={`p-2 rounded-xl ${isDark ? "bg-slate-900/40" : "bg-[#F5F3ED]"}`}>
            <p className={`text-[9px] uppercase tracking-wider ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>{bg ? m.label_bg : m.label_en}</p>
            <p className={`text-base font-bold ${isDark ? "text-white" : "text-[#2A342B]"}`}>{m.val ?? "—"}{m.val != null && m.unit ? <span className="text-[10px]">{m.unit}</span> : null}</p>
          </div>
        ))}
      </div>
      {actions.length > 0 && (
        <div className={`p-2.5 rounded-xl border-l-4 border-emerald-500 ${isDark ? "bg-emerald-950/20" : "bg-emerald-50"}`}>
          <p className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
            {bg ? "PMOS препоръки" : "PMOS recommendations"}
          </p>
          <ul className="space-y-0.5">
            {actions.map((a, i) => <li key={i} className={`text-xs ${isDark ? "text-emerald-100" : "text-emerald-900"}`}>• {bg ? a.bg : a.en}</li>)}
          </ul>
        </div>
      )}
      <SourceAttributionChip source="Хранителен дневник" freshness={data?.n_days ? `${data.n_days} дни` : null} isDark={isDark} />
    </div>
  );
}

function IntelCardBody({ data, bg, isDark, API }) {
  const [insights, setInsights] = useState([]);
  useEffect(() => {
    axios.get(`${API}/wh/pmos-program/intel-card`).then(({ data: d }) => setInsights(d.insights || [])).catch(() => {});
  }, [API]);
  if (!data?.has_data) {
    return <p className={`text-xs ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>{bg ? "Все още няма данни от Health Intelligence." : "No data from Health Intelligence yet."}</p>;
  }
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
        {[
          { k: "bmi",   label_bg: "BMI", label_en: "BMI", val: data.bmi },
          { k: "waist", label_bg: "Талия", label_en: "Waist", val: data.waist_cm, unit: "см" },
          { k: "vais",  label_bg: "VAIS", label_en: "VAIS", val: data.vais },
          { k: "weight",label_bg: "Тегло", label_en: "Weight", val: data.weight_kg, unit: "кг" },
        ].map((m) => (
          <div key={m.k} className={`p-2 rounded-xl ${isDark ? "bg-slate-900/40" : "bg-[#F5F3ED]"}`}>
            <p className={`text-[9px] uppercase tracking-wider ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>{bg ? m.label_bg : m.label_en}</p>
            <p className={`text-base font-bold ${isDark ? "text-white" : "text-[#2A342B]"}`}>{m.val ?? "—"}{m.val != null && m.unit ? <span className="text-[10px]"> {m.unit}</span> : null}</p>
          </div>
        ))}
      </div>
      {insights.length > 0 && (
        <div className={`p-2.5 rounded-xl border-l-4 border-violet-500 ${isDark ? "bg-violet-950/20" : "bg-violet-50"}`}>
          <ul className="space-y-0.5">
            {insights.map((i, idx) => <li key={idx} className={`text-xs ${isDark ? "text-violet-100" : "text-violet-900"}`}>• {bg ? i.bg : i.en}</li>)}
          </ul>
        </div>
      )}
      <SourceAttributionChip source="Здравна интелигентност · Тяло" freshness={data?.last_update || null} isDark={isDark} />
    </div>
  );
}

function CycleCardBody({ data, bg, isDark, API }) {
  const [interpret, setInterpret] = useState([]);
  useEffect(() => {
    axios.get(`${API}/wh/pmos-program/cycle-card`).then(({ data: d }) => setInterpret(d.interpretation || [])).catch(() => {});
  }, [API]);
  if (!data?.has_data) {
    return <p className={`text-xs ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>{bg ? "Все още няма данни за цикъла." : "No cycle data yet."}</p>;
  }
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-1.5">
        <div className={`p-2 rounded-xl ${isDark ? "bg-slate-900/40" : "bg-[#F5F3ED]"}`}>
          <p className={`text-[9px] uppercase tracking-wider ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>{bg ? "Среден" : "Avg"}</p>
          <p className={`text-base font-bold ${isDark ? "text-white" : "text-[#2A342B]"}`}>{data.avg_length_days ?? "—"} {bg ? "д" : "d"}</p>
        </div>
        <div className={`p-2 rounded-xl ${isDark ? "bg-slate-900/40" : "bg-[#F5F3ED]"}`}>
          <p className={`text-[9px] uppercase tracking-wider ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>σ</p>
          <p className={`text-base font-bold ${isDark ? "text-white" : "text-[#2A342B]"}`}>{data.regularity_std ?? "—"}</p>
        </div>
        <div className={`p-2 rounded-xl ${isDark ? "bg-slate-900/40" : "bg-[#F5F3ED]"}`}>
          <p className={`text-[9px] uppercase tracking-wider ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>{bg ? "Цикли" : "Cycles"}</p>
          <p className={`text-base font-bold ${isDark ? "text-white" : "text-[#2A342B]"}`}>{data.n_cycles}</p>
        </div>
      </div>
      {interpret.length > 0 && (
        <div className={`p-2.5 rounded-xl border-l-4 border-pink-500 ${isDark ? "bg-pink-950/20" : "bg-pink-50"}`}>
          <ul className="space-y-0.5">
            {interpret.map((i, idx) => <li key={idx} className={`text-xs ${isDark ? "text-pink-100" : "text-pink-900"}`}>• {bg ? i.bg : i.en}</li>)}
          </ul>
        </div>
      )}
      <SourceAttributionChip source="Менструален цикъл" freshness={data?.n_cycles ? `${data.n_cycles} цикъла` : null} isDark={isDark} />
    </div>
  );
}

function EducationCardBody({ data, bg, isDark }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {(data.topics || []).map((t) => (
        <div key={t.key} className={`p-3 rounded-xl ${isDark ? "bg-slate-900/40" : "bg-[#F5F3ED]"}`} data-testid={`pmos-topic-${t.key}`}>
          <p className={`text-sm font-bold mb-1 ${isDark ? "text-white" : "text-[#2A342B]"}`}>{bg ? t.bg : t.en}</p>
          <p className={`text-xs ${isDark ? "text-slate-300" : "text-[#5C665D]"}`}>{bg ? t.summary_bg : t.summary_en}</p>
        </div>
      ))}
    </div>
  );
}

/* ───── Insights ───── */
function PmosInsights({ insights, bg, isDark }) {
  if (!insights?.insights?.length) return null;
  const CONF = { high: { border: "border-emerald-500", bg_l: "bg-emerald-50", bg_d: "bg-emerald-950/30" }, moderate: { border: "border-amber-500", bg_l: "bg-amber-50", bg_d: "bg-amber-950/30" }, low: { border: "border-slate-400", bg_l: "bg-slate-50", bg_d: "bg-slate-900/40" } };
  return (
    <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"} data-testid="pmos-insights-card">
      <CardContent className="p-4 sm:p-5">
        <p className={`text-[10px] uppercase tracking-wider font-bold mb-3 ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
          <Lightbulb className="w-3 h-3 inline mr-1" />
          {bg ? "PMOS наблюдения" : "PMOS observations"}
        </p>
        <div className="space-y-2">
          {insights.insights.map((ins) => {
            const c = CONF[ins.confidence] || CONF.moderate;
            return (
              <div key={ins.id} className={`p-3 rounded-xl border-l-4 ${c.border} ${isDark ? c.bg_d : c.bg_l}`} data-testid={`pmos-insight-${ins.id}`}>
                <div className="flex items-start justify-between flex-wrap gap-1 mb-1">
                  <p className={`text-sm font-bold ${isDark ? "text-white" : "text-[#2A342B]"}`}>
                    {bg ? ins.observation_bg : ins.observation_en}
                  </p>
                  <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${isDark ? "bg-slate-800 text-slate-300" : "bg-white text-slate-600 border border-slate-200"}`}>
                    {bg ? ins.confidence_bg : ins.confidence_en}
                  </span>
                </div>
                <p className={`text-xs italic mb-1 ${isDark ? "text-slate-300" : "text-[#5C665D]"}`}>
                  {bg ? ins.interpretation_bg : ins.interpretation_en}
                </p>
                <p className={`text-xs ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                  <b>→</b> {bg ? ins.action_bg : ins.action_en}
                </p>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

/* ───── KPIs ───── */
function ProgressKpis({ progress, bg, isDark }) {
  if (!progress) return null;
  return (
    <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"} data-testid="pmos-kpi-card">
      <CardContent className="p-4 sm:p-5">
        <p className={`text-[10px] uppercase tracking-wider font-bold mb-3 ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
          {bg ? "Първични резултати" : "Primary outcomes"}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
          {progress.primary.map((k) => (
            <div key={k.key} className={`p-3 rounded-xl ${isDark ? "bg-slate-900/40" : "bg-[#F5F3ED]"}`} data-testid={`pmos-kpi-${k.key}`}>
              <p className={`text-[10px] uppercase tracking-wider font-bold mb-0.5 ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
                {bg ? k.label_bg : k.label_en}
              </p>
              <div className="flex items-center justify-between">
                <p className={`text-lg font-bold ${isDark ? "text-white" : "text-[#2A342B]"}`}>{k.value}</p>
                <span className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>
                  {bg ? k.status_bg : k.status_en}
                </span>
              </div>
            </div>
          ))}
        </div>
        <details>
          <summary className={`text-[10px] uppercase tracking-wider font-bold cursor-pointer ${isDark ? "text-slate-500" : "text-[#8F9990]"}`}>
            {bg ? "Допълнителни числа (за справка)" : "Additional numbers (reference)"}
          </summary>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 mt-2">
            {Object.entries(progress.secondary).map(([k, v]) => {
              const SECONDARY_LABEL = {
                nis: bg ? "Хран. индекс" : "NIS",
                dil: bg ? "Възпален. ефект" : "DIL",
                uis: bg ? "Ултрапреработени" : "UIS",
                fiber_g: bg ? "Фибри (г)" : "Fiber (g)",
                protein_g: bg ? "Белтъци (г)" : "Protein (g)",
                bmi: "BMI",
                waist_cm: bg ? "Талия (см)" : "Waist (cm)",
                vais: "VAIS",
              };
              return (
                <div key={k} className={`p-1.5 rounded-lg ${isDark ? "bg-slate-900/30" : "bg-[#F5F3ED]"}`}>
                  <p className={`text-[10px] ${isDark ? "text-slate-500" : "text-[#8F9990]"}`}>{SECONDARY_LABEL[k] || k}</p>
                  <p className={`text-xs font-bold ${isDark ? "text-slate-300" : "text-[#5C665D]"}`}>{v ?? "—"}</p>
                </div>
              );
            })}
          </div>
        </details>
        <p className={`text-[10px] italic mt-2 ${isDark ? "text-slate-500" : "text-[#5C665D]"}`}>
          {bg ? progress.disclaimer_bg : progress.disclaimer_en}
        </p>
      </CardContent>
    </Card>
  );
}
